import { create } from "zustand";

import { LocationItem, MapStore } from "./types";

const trimValue = (value?: string | null) => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
};

const normalizeOwner = (owner?: any) => {
  if (!owner) return owner;

  const normalizedDisplayName =
    trimValue(owner.username) ||
    trimValue(owner.email) ||
    "Unknown";

  return {
    ...owner,
    displayName: normalizedDisplayName,
  };
};

const enhanceLocation = (location: any, actionTitle: string) => {
  const normalizedOwner = normalizeOwner(location?.owner);

  return {
    ...location,
    actionTitle,
    ...(normalizedOwner ? { owner: normalizedOwner } : {}),
  };
};

export const useMapStore = create<MapStore>((set, get) => ({
  onMapChange: null,
  setOnMapChange: (onMapChange: MapStore["onMapChange"]) =>
    set({ onMapChange }),

  canAddMarker: false,
  handlerCanAddMarker: () =>
    set((state) => ({
      canAddMarker: !state.canAddMarker,
      markerPosition: null,
    })),

  locationsList: [],

  addLocations: (id: string, item: LocationItem) => {
    const normalizedItem = {
      ...item,
      locations: item.locations.map((location: any) =>
        enhanceLocation(location, item.title),
      ),
    };

    set((state) => ({
      locationsList: state.locationsList.some((loc) => loc.id === id)
        ? state.locationsList.map((loc) =>
            loc.id === id ? { id, item: normalizedItem } : loc,
          )
        : [...state.locationsList, { id, item: normalizedItem }],
    }));
  },

  addAllLocations: (locations: { id: string; item: LocationItem }[]) => {
    const normalizedLocations = locations.map(({ id, item }) => ({
      id,
      item: {
        ...item,
        locations: item.locations.map((location: any) =>
          enhanceLocation(location, item.title),
        ),
      },
    }));

    set(() => ({
      locationsList: normalizedLocations,
    }));
  },

  updateLocationsList: (newLocation: LocationItem) =>
    set((state) => ({
      locationsList: state.locationsList.map((obj) =>
        obj.id === state.activeId
          ? {
              ...obj,
              item: {
                ...obj.item,
                locations: obj.item.locations.map((location: any) =>
                  location.id === newLocation.id
                    ? enhanceLocation(
                        { ...location, ...newLocation },
                        obj.item.title,
                      )
                    : location,
                ),
              },
            }
          : obj,
      ),
    })),

  removeLocationById: (locationId: string) =>
    set((state) => ({
      locationsList: state.locationsList.map((obj) =>
        obj.id === state.activeId
          ? {
              ...obj,
              item: {
                ...obj.item,
                locations: obj.item.locations.filter(
                  (location: any) => location.id !== locationId, // Remove o item pelo ID
                ),
              },
            }
          : obj,
      ),
    })),

  getAllLocations: (id?: string | null) => {
    const { locationsList, activeId } = get();

    const activeIdToUse = id || activeId;

    if (activeIdToUse === "all") {
      return locationsList.flatMap((obj) => obj.item.locations || []);
    }

    const activeItem = locationsList.find((obj) => obj.id === activeIdToUse);
    return activeItem?.item.locations || [];
  },

  getAllFocusAreas: (id?: string | null) => {
    const { locationsList, activeId } = get();

    const activeIdToUse = id || activeId;

    if (activeIdToUse === "all") {
      return locationsList.flatMap((obj) => 
        (obj.item.focusAreas || []).map(focusArea => ({
          ...focusArea,
          actionPlanTitle: obj.item.title,
        }))
      );
    }

    const activeItem = locationsList.find((obj) => obj.id === activeIdToUse);
    return (activeItem?.item.focusAreas || []).map(focusArea => ({
      ...focusArea,
      actionPlanTitle: activeItem?.item.title,
    }));
  },

  getDetailLocationsByActiveId: (id?: string) => {
    const location = get().locationsList.find(
      (loc) => loc.id === (id || get().activeId),
    );
    return location?.item;
  },

  activeId: null,
  setActiveId: (activeId: string | null) => set({ activeId }),

  resetCenterLocationsMap: false,
  handleResetCenterLocationsMap: () =>
    set({
      resetCenterLocationsMap: !get().resetCenterLocationsMap,
      markerPosition: null,
    }),

  markerPosition: null,
  setMarkerPosition: (markerPosition: { lat: number; lng: number } | null) =>
    set({ markerPosition }),

  mapRef: null,
  setMapRef: (map: any) => set({ mapRef: map }),

  newMarkerRef: null,
  setNewMarkerRef: (newMarkerRef: any) => set({ newMarkerRef }),

  markersRef: null,
  setMarkersRef: (markersRef: any) => set({ markersRef }),

  trackThisTask: null,
  setTrackThisTask: (trackThisTask: any) =>
    set({ markerPosition: null, trackThisTask }),

  onCompleteHandleLocation: false,
  setOnCompleteHandleLocation: () =>
    set({
      onCompleteHandleLocation: !get().onCompleteHandleLocation,
    }),

  origin: "action",
  setOrigin: (origin: "action" | "resource") => set({ origin }),

  originToActiveIdMap: [],
  setOriginToActiveIdMap: (originToActiveIdMap) => set({ originToActiveIdMap }),
  
  tabsActiveTab: { tab: "tab1", animatedToggle: false },
  setTabsActiveTab: ({
    tab,
    animatedToggle,
  }: {
    tab: "tab1" | "tab2";
    animatedToggle: boolean;
  }) => set({ tabsActiveTab: { tab, animatedToggle } }),

  mobilePageViewIndex: 1,
  setMobilePageViewIndex: (mobilePageViewIndex: number) =>
    set({ mobilePageViewIndex }),
}));
